import { type InputHTMLAttributes, type Ref, useId } from 'react';
import { cn } from '../../lib/utils';
import { FieldError } from './FieldError';
import { FIELD_LABEL_TYPOGRAPHY } from './FieldLabel';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  controlSize?: 'sm' | 'md';
  error?: string;
}

export function Checkbox({ ref, className, label, error, id, controlSize = 'sm', ...props }: CheckboxProps & { ref?: Ref<HTMLInputElement> }) {
  const autoId = useId();
  const resolvedId = id ?? autoId;
  const errorId = `${resolvedId}-error`;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className={cn('relative inline-flex shrink-0', controlSize === 'md' ? 'h-6 w-6' : 'h-4 w-4', className)}>
          <input
            ref={ref}
            id={resolvedId}
            type="checkbox"
            className={cn(
              'peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none rounded',
              'focus-visible:outline-none disabled:cursor-not-allowed',
            )}
            {...props}
            aria-invalid={error ? true : props['aria-invalid']}
            aria-describedby={error ? [errorId, props['aria-describedby']].filter(Boolean).join(' ') : props['aria-describedby']}
          />
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none h-full w-full rounded border bg-bg-primary shadow-xs transition-colors',
              'peer-checked:border-primary peer-checked:bg-primary',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/25 peer-focus-visible:ring-offset-1',
              'peer-disabled:bg-bg-subtle peer-disabled:opacity-50',
              error ? 'border-error' : 'border-border',
            )}
          />
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.25}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[70%] -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100 peer-disabled:opacity-50"
          >
            <path d="M3 8.25 6.25 11.5 13 4.75" />
          </svg>
        </span>
        {label && (
          <label htmlFor={resolvedId} className={`${FIELD_LABEL_TYPOGRAPHY} text-secondary`}>
            {label}
          </label>
        )}
      </div>
      <FieldError id={errorId} error={error} />
    </div>
  );
}
